import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { SITE } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const topics = [
  "TOEIC Testing / Booking",
  "Test Results",
  "App Development Project",
  "Blue Ledger Accounting Software",
  "Sister Islands Testing",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you within one business day.");
      setForm({ name: "", email: "", phone: "", topic: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <div className="bg-[#0A2540] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-[#39C1D0] font-bold text-sm uppercase tracking-widest mb-3">Get in Touch</div>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">Contact Us</h1>
          <p className="mt-5 text-slate-300 max-w-2xl mx-auto">
            Questions about testing, app development or Blue Ledger? We'd love to hear from you.
          </p>
        </div>
      </div>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-5 gap-10">
          {/* Info */}
          <div className="lg:col-span-2 space-y-5">
            {[
              { icon: MapPin, title: "Our Location", lines: [SITE.address, SITE.addressNote] },
              { icon: Phone, title: "Phone", lines: [SITE.phone] },
              { icon: Mail, title: "Email", lines: [SITE.email, `Sister Islands: ${SITE.sisterIslandsEmail}`] },
              { icon: Clock, title: "Hours of Operation", lines: [SITE.hours] },
            ].map((c) => (
              <Card key={c.title} className="rounded-2xl border-slate-100 shadow-sm">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#E8F7F9] flex items-center justify-center shrink-0">
                    <c.icon className="text-[#0E9AAA]" size={20} />
                  </div>
                  <div>
                    <div className="font-bold text-[#0A2540]">{c.title}</div>
                    {c.lines.map((l) => (
                      <p key={l} className="text-sm text-slate-600 mt-0.5">{l}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
            <div className="rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
              <iframe
                title="MBS Location Map"
                src="https://www.google.com/maps?q=93+Hospital+Rd,+George+Town,+Cayman+Islands&output=embed"
                className="w-full h-64 border-0"
                loading="lazy"
              />
            </div>
          </div>

          {/* Form */}
          <Card className="lg:col-span-3 rounded-2xl border-slate-100 shadow-sm h-fit">
            <CardContent className="p-8">
              <h2 className="font-extrabold text-[#0A2540] text-2xl mb-6">Send Us a Message</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="font-bold text-[#0A2540]">Your Name *</Label>
                  <Input
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 rounded-lg"
                  />
                </div>
                <div>
                  <Label className="font-bold text-[#0A2540]">Email *</Label>
                  <Input
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 rounded-lg"
                  />
                </div>
                <div>
                  <Label className="font-bold text-[#0A2540]">Phone</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-2 rounded-lg"
                  />
                </div>
                <div>
                  <Label className="font-bold text-[#0A2540]">Topic</Label>
                  <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                    <SelectTrigger className="mt-2 rounded-lg">
                      <SelectValue placeholder="Select a topic" />
                    </SelectTrigger>
                    <SelectContent>
                      {topics.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="font-bold text-[#0A2540]">Message *</Label>
                  <Textarea
                    data-testid="contact-message-input"
                    rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-2 rounded-lg"
                  />
                </div>
              </div>
              <Button
                data-testid="contact-submit-btn"
                onClick={handleSubmit}
                disabled={sending}
                className="mt-6 bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg px-8 h-11 shadow-md"
              >
                {sending ? "Sending..." : <>Send Message <Send size={15} className="ml-2" /></>}
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
